import { ChatMessage } from '../components/AiChat/types';
import { Status } from '../components/TestCharts/FreshnessChartCommon';
import { ENTITY_QUERY_KEYS } from '../constants/queryKeys';

export type EntityQueryKey = keyof typeof ENTITY_QUERY_KEYS;

export type DbtResourceType =
  | 'model'
  | 'source'
  | 'exposure'
  | 'seed'
  | 'snapshot';

export type ModelArtifactType =
  | DbtResourceType
  | 'column'
  | 'semantic_view'
  | 'connector';

export type ModelType =
  | 'dashboard'
  | 'notebook'
  | 'analysis'
  | 'ml'
  | 'application';

export const BI_ASSET_TYPES = [
  'dashboard',
  'Dashboard',
  'page',
  'workbook',
  'element',
  'insight',
  'cell',
  'project',
  'report',
  'dataset',
  'liveboard',
  'answer',
  'explore',
  'chart_tile',
  'sql_chart_tile',
  'Published Data Source',
  'Embedded Data Source',
] as const;

export type BiSubType = typeof BI_ASSET_TYPES[number];

export type AssetDisplayType = ModelArtifactType | BiSubType;

export type ApiAssetType =
  | 'TABLE'
  | 'EXPOSURE'
  | 'COLUMN'
  | 'EPHEMERAL'
  | 'SEMANTIC'
  | 'ETL';

export type AssetLevel = 'NODE' | 'SUB_NODE' | 'COLLECTION' | 'EPHEMERAL';

export type Model = {
  name: string;
  unique_id: string;
  owners: string[];
  tags: string[];
  package_name: string;
  description?: string;
  full_path: string;
  meta: Record<string, any>;
  database_name?: string;
  schema_name?: string;
  table_name?: string;
  model_name: string;
  normalized_full_path: string;
  fqn: string;
  artifact_type: DbtResourceType;
  source_name?: string;
  label?: string;
  url?: string;
  type?: ModelType;
  maturity?: string;
  depends_on?: string;
  owner?: string;
  patch_path?: string;
  materialization?: string;
};

type ExpectedConfig = {
  min_training_set_size: number;
  seasonality?: string;
  days_back: number;
  backfill_days: number;
  anomaly_direction: 'spike' | 'both' | 'drop';
  anomaly_sensitivity: number;
  where_expression?: string;
  timestamp_column?: string;
  time_bucket: Period;
};

type TestConfigParams = {
  expected_config?: ExpectedConfig;
  model?: string;
  min_training_set_size?: number;
  days_back?: number;
  backfill_days?: number;
  seasonality?: string;
  anomaly_direction?: 'spike' | 'both' | 'drop';
  anomaly_sensitivity?: number;
  where_expression?: string;
  timestamp_column?: string;
  time_bucket?: Period;
  detection_period?: Period;
  to?: string;
  field?: string;
  column_name?: string;
};

export type Period = {
  period: 'day' | 'hour' | 'week' | 'month' | 'minute' | 'second';
  count: number;
};

type TestParams = {
  expected_config?: ExpectedConfig;
  model?: string;
  min_training_set_size?: number;
  days_back?: number;
  backfill_days?: number;
  detection_period?: Period;
  detection_delay?: Period;
  seasonality?: string;
  anomaly_direction?: 'spike' | 'both' | 'drop';
  anomaly_sensitivity?: number;
  where_expression?: string;
  timestamp_column?: string;
  time_bucket?: Period;
  exclude_regexp?: string;
  freshness_column?: string;
  event_timestamp_column?: string;
  dimensions?: string[];
  column_anomalies?: string[];
  column_name?: string;
  update_timestamp_column?: string;
  to?: string;
  field?: string;
  enforce_types?: boolean;
  fail_on_added?: boolean;
  sensitivity?: number;
  fail_on_zero?: boolean;
};

type ResultsSample = {
  num?: number;
  from_field?: string | number;
  min?: number;
  min_issue?: number;
};

export type Metrics = {
  value?: number;
  average?: number;
  min_value?: number | null;
  max_value?: number | null;
  start_time?: string;
  end_time?: string;
  id?: string;
  metric_id?: string;
  test_execution_id?: string;
  test_unique_id?: string;
  detected_at?: string;
  full_table_name?: string;
  column_name?: string;
  metric_name?: string;
  anomaly_score?: number;
  anomaly_score_threshold?: number;
  anomalous_value?: string;
  bucket_start?: string;
  bucket_end?: string;
  bucket_seasonality?: string | number;
  metric_value?: number;
  min_metric_value?: number;
  max_metric_value?: number;
  training_avg?: number;
  training_stddev?: number;
  training_set_size?: number;
  training_start?: string;
  training_end?: string;
  dimension?: string;
  dimension_value?: string;
  anomaly_description?: string;
  max_bucket_end?: string;
  is_anomalous?: boolean;
  headers?: { id: string; display_name: string; type: string }[];
  test_rows_sample?: {
    anomalous_value_timestamp: string;
    anomalous_value_row_count: number;
    anomalous_value_average_row_count: number;
    platform: string;
    version?: string;
  }[];
};

export type RunStatus =
  | 'pass'
  | 'fail'
  | 'warn'
  | 'error'
  | 'skipped'
  | 'no_data';
export type SourceFreshnessRunStatus = 'pass' | 'fail' | 'warn';

export enum AnomalyDirection {
  Drop = 'DROP',
  Spike = 'SPIKE',
  Both = 'BOTH',
}

export type TestConfiguration = {
  test_name: string;
  test_params?: TestConfigParams;
  timestamp_column?: string;
  testing_timeframe?: string;
  anomaly_threshold?: number;
  anomaly_direction?: AnomalyDirection;
  detection_period?: Period;
  sensitivity?: 'low' | 'medium' | 'high';
};

export type SourceFreshnessConfiguration = {
  error_after?:
    | { period: 'minute' | 'hour' | 'day'; count: number }
    | { period: null; count: null };
  warn_after?:
    | { period: 'minute' | 'hour' | 'day'; count: number }
    | { period: null; count: null };
  loaded_at_field?: string;
};

export type TestMetadata = {
  test_unique_id: string;
  elementary_unique_id: string;
  database_name: string;
  schema_name: string;
  table_name?: string;
  column_name?: string;
  test_name: string;
  test_tags?: string[];
  test_owners?: string[];
  test_display_name: string;
  latest_run_time: string;
  latest_run_time_utc?: string;
  latest_run_status: RunStatus | SourceFreshnessRunStatus;
  model_unique_id?: string;
  table_unique_id: string;
  test_type: string;
  test_sub_type: string;
  test_query?: string;
  test_params: TestParams;
  test_created_at?: string;
  description?: string;
  original_path?: string;
  normalized_full_path?: string;
  quality_dimension?: string;
  severity?: string;
  // Only undefined for source freshness results
  result?: {
    result_description?: string;
    result_query?: string;
    result_id?: string;
  };
  configuration: TestConfiguration | SourceFreshnessConfiguration;
};

export type CloudTestMetadata = TestMetadata & {
  test_owners: string[];
};

export enum TotalTypes {
  errors = 'errors',
  warnings = 'warnings',
  passed = 'passed',
  failures = 'failures',
  noData = 'noData',
}

export type Totals = {
  [key in TotalTypes]: number;
};

export type TreeTotalsType = 'folder' | 'asset' | 'test';

export type TreeTotalsNode = {
  id: string;
  kind: TreeTotalsType;
  requestKey: string;
};

export type TestResultsGroup = {
  [key in TotalTypes]: string[];
};

export type Test = {
  unique_id: string;
  model_unique_id?: string;
  table_unique_id?: string;
  database_name?: string;
  schema_name: string;
  table_name?: string;
  column_name?: string;
  name: string;
  display_name: string;
  original_path?: string;
  type: string;
  test_type?: string;
  test_sub_type?: string;
  test_params: Record<string, any>;
  description?: string;
  configuration: Record<string, any>;
  tags: string[];
  normalized_full_path?: string;
  created_at?: string;
  latest_run_time?: string;
  latest_run_time_utc?: string;
  latest_run_status?: RunStatus | SourceFreshnessRunStatus;
};

type TestResults = {
  display_name: string;
  results_sample?: ResultsSample[];
  error_message?: string;
  failed_rows_count?: number;
  metrics?: Metrics[] | Metrics;
  result_description?: string;
};

type AssetMetadata = Partial<{
  last_data_modification_time: string;
  materialization: string;
  owners: string[];
  tags: string[];
  is_critical: boolean;
  path: string;
  name: string;
  subscribers: string[];
}>;

export type TestResult = {
  metadata: TestMetadata | CloudTestMetadata;
  test_results: TestResults;
  asset_metadata?: AssetMetadata;
};

export type SourceFreshnessTestResult = {
  metadata: TestMetadata;
  test_results: SourceFreshnessTestResults;
};

export type TestInvocation = {
  affected_rows?: number;
  time_utc: string;
  id: string;
  status: 'pass' | 'fail' | 'warn' | 'error';
};

export type TestRun = {
  metadata: TestMetadata | CloudTestMetadata;
  test_runs: {
    fail_rate: number;
    totals: Totals;
    invocations: TestInvocation[];
    description: string;
  };
  last_exec_time: number;
  median_exec_time: number;
  asset_metadata?: AssetMetadata;
};

type SourceFreshnessTestResults = {
  status: 'pass' | 'fail' | 'warn' | 'error';
  error_message: string | null;
  max_loaded_at: string | null;
  max_loaded_at_time_ago_in_s: number | null;
  detected_at: string | null;
};

export type SourceFreshnessTestRun = TestRun & {
  test_results: SourceFreshnessTestResults;
};

export type ModelRun = {
  id: string;
  time_utc: string;
  status: 'success' | 'error' | 'skipped';
  full_refresh: boolean;
  materialization: string;
  execution_time: number;
};

export type ModelRuns = {
  unique_id: string;
  database_name?: string;
  schema: string;
  name: string;
  status: 'success' | 'error' | 'skipped';
  last_exec_time: number;
  median_exec_time: number;
  exec_time_change_rate: number;
  totals: { errors: number; success: number };
  runs: ModelRun[];
  last_generated_at: string;
  compiled_code: string | null;

  asset_metadata?: AssetMetadata;
};

export type TreeLeaf = {
  node_id: string;
  resource_type:
    | 'model'
    | 'source'
    | 'exposure'
    | 'test'
    | 'seed'
    | 'snapshot'
    | 'table'
    | 'view';
};

export interface TreeNode {
  [key: string]: TreeNode | TreeLeaf[];
}

export type Groups = {
  [key: string]: { name: string; data: TreeNode }[] | null;
};

export type ApiTreeLeaf = TreeLeaf & {
  label: string;
  is_ephemeral: boolean;
  totals?: Totals;
};

export interface ApiTreeNode {
  [key: string]: ApiTreeNode | ApiTreeLeaf[];
}

export type Invocation = {
  invocation_id?: string;
  detected_at?: string;
  command?: string;
  selected?: string;
  full_refresh?: boolean;
  job_id?: string;
  job_name?: string;
  job_url?: string;
  job_run_id?: string;
  job_run_url?: string;
  orchestrator?: string;
};

type Coverages = {
  table_tests: number;
  column_tests: number;
};

export type TestFilter = {
  name: 'failures' | 'warnings' | 'errors' | 'passed' | 'no_test';
  display_name: 'Failures' | 'Warnings' | 'Errors' | 'Passed';
  model_unique_ids: string[];
};

export type ModelFilter = {
  name: 'success' | 'errors';
  display_name: 'Successful Runs' | 'Failed Runs';
  model_unique_ids: string[];
};

export type Filters = {
  test_results: TestFilter[];
  test_runs: TestFilter[];
  model_runs: ModelFilter[];
};

export type LineageNode = {
  id: string;
  type: ModelArtifactType;
  parent?: string;
};

export type Lineage = {
  nodes: LineageNode[];
  edges: Array<[string, string]>;
};

type Env = {
  project_name: string;
  env: string;
  warehouse_type?: string;
};

type Tracking = {
  posthog_api_key: string;
  report_generator_anonymous_user_id: string;
  anonymous_warehouse_id: string;
};

export type TestsRunsCountByHour = {
  [date: string]: { [status: string]: number };
};

export type FetchReportApi = {
  creation_time: string;
  days_back: number;
  models: Record<string, Model>;
  groups: Groups;
  invocation: Invocation;
  invocations: Invocation[];
  tests: Record<string, Test>;
  test_results: Record<string, TestResult[]>;
  test_results_totals: Record<string, Totals>;
  test_runs: Record<string, TestRun[]>;
  test_runs_totals: Record<string, Totals>;
  coverages: Record<string, Coverages>;
  model_runs: ModelRuns[];
  model_runs_totals: Record<string, Totals>;
  filters: Filters; // todo: can this be removed from the report api?
  lineage: Lineage;
  env: Env;
  tracking: Tracking;
  resources_latest_invocation: Record<string, string>;
  invocations_job_identification: Record<string, string[]>;
  test_runs_counts_by_hour?: TestsRunsCountByHour;
};

export type Report = FetchReportApi;

export type AnomalySensitivity = 'low' | 'medium' | 'high';

export type AutomatedTestAnomalyConfig = {
  sensitivity: AnomalySensitivity;
  anomaly_direction: 'BOTH' | 'DROP' | 'SPIKE';
  detection_period: Period;
  fixed_threshold?: Period | null;
  training_period?: Period;
  excluded_time_ranges?: Array<{
    start_time: string;
    end_time: string;
  }>;
  spike_threshold?: number | null;
  drop_threshold?: number | null;
};

export type AutomatedTestConfigurationApi = {
  type: 'anomaly_detection' | 'freshness_anomaly_detection';
  anomaly_config: AutomatedTestAnomalyConfig;
  severity: number;
  owners: string[];
  tags: string[];
};

export type DataContractConfigApi = {
  baseline_mode: 'manual' | 'complete';
  baseline_columns: Array<{
    column_name: string;
    data_type: string;
    is_nullable: boolean;
  }> | null;
  enforcement_level: 'all' | 'breaking' | 'downstream' | 'downstream_critical';
};

export type DataContractFormValues = {
  baselineMode: DataContractConfigApi['baseline_mode'];
  baselineColumns: NonNullable<DataContractConfigApi['baseline_columns']>;
  enforcementLevel: DataContractConfigApi['enforcement_level'];
};

export type AnomalyDetectionChart = {
  metrics: Metrics[];
  metricsTitle: string;
  failOnZero?: boolean | undefined;
  anomalyDirection?: AnomalyDirection | undefined;
  detectionPeriod?: Period;
  detectionDelay?: Period;
  status: RunStatus;
};

export type FreshnessAnomalyDetectionChart = {
  metrics: Metrics[];
  status: Status;
};

export type SensitivityEstimation = {
  sensitivity: AnomalySensitivity;
  min?: number;
  max?: number;
};

export type FetchMetadataValuesApi = {
  tags: Array<string>;
  owners: Array<string>;
  subscribers: Array<string>;
  asset_names: Array<string>;
  database_names: Array<string>;
  schema_names: Array<string>;
};

export type UpdateTestApi = {
  disabled?: boolean;
  owners?: string[];
  tags?: string[];
};

export type UpdateCloudTestResponse = {
  test_id: string;
};

export type UpdateCloudTestsResponse = Array<UpdateCloudTestResponse>;

export type TestPollingApi = {
  latest_result_id?: string;
  executed_with_new_config: boolean;
};

export type TreeType = 'bi_assets' | 'data_assets';
export type TreeViewBy = 'tags' | 'owners' | 'dwh' | 'paths' | 'dbt';

export type ValueLabelOption = {
  label: string;
  value: string;
};

export type ValueLabelIconOption = ValueLabelOption & {
  icon: JSX.Element;
};

export type AccountPermission =
  | 'VIEW_USERS'
  | 'EDIT_USERS'
  | 'VIEW_ROLES'
  | 'EDIT_ROLES'
  | 'CREATE_ENVIRONMENTS'
  | 'DELETE_ENVIRONMENTS'
  | 'VIEW_AUDIT_LOGS'
  | 'VIEW_SYSTEM_LOGS'
  | 'SET_TIMEZONE'
  | 'VIEW_INTEGRATIONS'
  | 'EDIT_INTEGRATIONS'
  | 'TEST_INTEGRATIONS'
  | 'VIEW_GROUPS'
  | 'EDIT_GROUPS'
  | 'VIEW_AI_RULES'
  | 'EDIT_AI_RULES'
  | 'VIEW_AI_USAGE';

export type UserSessionUsage = {
  input_tokens: number;
  output_tokens: number;
  used_usd: number;
  limit_usd: number;
  exhausted: boolean;
  session_started_at: string | null;
  resets_at: string | null;
  monthly_limit_reached?: boolean;
  monthly_resets_at?: string | null;
};

export type FetchAccountApi = {
  id: string;
  name: string;
  activation_status: string;
  cloud_tests_enabled: boolean;
  use_dashboard_api: boolean;
  ai_features_enabled: boolean;
  ai_agents?: {
    catalog: boolean;
    incidents: boolean;
    test_recommendations: boolean;
    test_configuration: boolean;
    optimization: boolean;
  };
  new_agents_infra?: boolean;
  activity_logs_enabled: boolean;
  demo: boolean;
  use_assets_api: boolean;
  use_tests_api?: boolean;
  roles_and_permissions_enabled: boolean;
  has_scim_configuration: boolean;
  resource_permissions_enabled: boolean;
  incidents_v2_enabled: boolean;
  permissions?: AccountPermission[];
};

export type ChatHistoryItemResponse = {
  thread_id: string;
  chat_name: string;
  user_id: string;
  conversation_starter: string;
  model: string;
  total_tokens: number;
  time: string;
};

export type ChatHistoryResponse = {
  chats: ChatHistoryItemResponse[];
  total_count: number;
};

export type ChatMessagesResponse = {
  chat_id: string;
  chat_name: string;
  messages: ChatMessage[];
  environment_ids: string[];
  time: string;
};

export type SessionStatus =
  | 'queued'
  | 'running'
  | 'waiting_for_input'
  | 'idle'
  | 'cancelled'
  | 'failed';

export type SessionHistoryItemResponse = {
  session_id: string;
  status: SessionStatus;
  agent_type: string;
  trigger_source: string;
  conversation_starter: string | null;
  created_at: string;
};

export type SessionHistoryResponse = {
  sessions: SessionHistoryItemResponse[];
};

export type WarehouseType =
  | 'snowflake'
  | 'redshift'
  | 'bigquery'
  | 'databricks'
  | 'postgres'
  | 'athena'
  | 'clickhouse'
  | 'dremio'
  | 'fabric';

export type EnvironmentType = 'prod' | 'dev' | 'test';

export type EnvironmentPermission =
  | 'VIEW'
  | 'EDIT_CONFIG'
  | 'VIEW_ALERT_RULES'
  | 'EDIT_ALERT_RULES'
  | 'VIEW_DIGEST_RULES'
  | 'EDIT_DIGEST_RULES'
  | 'VIEW_PULL_REQUESTS'
  | 'EDIT_PULL_REQUESTS'
  | 'VIEW_TEST_CONFIGURATIONS'
  | 'EDIT_TEST_CONFIGURATIONS'
  | 'VIEW_INCIDENTS'
  | 'EDIT_INCIDENTS'
  | 'VIEW_METRICS'
  | 'VIEW_ASSETS'
  | 'EDIT_ASSETS'
  | 'VIEW_TESTS'
  | 'EDIT_TESTS'
  | 'TRIGGER_SYNC'
  | 'EDIT_SAVED_VIEWS'
  | 'SEND_MESSAGES';

export type SlimDwhConnection = {
  warehouseType: WarehouseType;
};

export type AccountEnvironmentBase = {
  id: string;
  name: string;
  environmentType?: EnvironmentType;
  lastSyncStatus?: 'success' | 'error' | 'running' | null;
  lastSyncedAt?: string | null;
  webhookSetup: boolean;
  isDeactivated: boolean;
  cron?: string | null;
  permissions?: EnvironmentPermission[];
  chatModes?: string[];
};

export type AccountEnvironment = AccountEnvironmentBase & {
  dwhConnection: SlimDwhConnection;
};

export type FetchEnvironmentsApi = Array<AccountEnvironment>;

export type AgentContext = {
  environmentIds?: string[];
  incidentIds?: string[];
  assetIds?: string[];
  testIds?: string[];
  displayLabels?: string[];
};

export type ComponentSize = 'extraSmall' | 'medium' | 'large';

export const COMPONENT_SIZE_MAP: Record<ComponentSize, number> = {
  extraSmall: 28,
  medium: 32,
  large: 40,
};

export type FieldChange<T> = { current: T; new: T };

export type AgentTestOperation = 'update' | 'create' | 'delete';

export type AgentCloudTestConfigFields = {
  sensitivity?: FieldChange<string | null>;
  threshold?: FieldChange<Period | null>;
  anomalyDirection?: FieldChange<string | null>;
  detectionPeriod?: FieldChange<Period | null>;
  trainingPeriod?: FieldChange<Period | null>;
  severity?: FieldChange<string | null>;
  owners?: FieldChange<string[]>;
  tags?: FieldChange<string[]>;
  enabled?: FieldChange<boolean>;
  muted?: FieldChange<boolean>;
};

export type AgentCloudTestUpdateChange = {
  testId: string;
} & AgentCloudTestConfigFields;

export type AgentCloudTestCreationType = 'freshness' | 'volume';

export type AgentCloudTestCreateChange = {
  assetId: string;
  testType: AgentCloudTestCreationType;
} & AgentCloudTestConfigFields;

export type AgentCloudTestConfigChange =
  | AgentCloudTestUpdateChange
  | AgentCloudTestCreateChange;

export type AgentTestConfigPlan<TChange = AgentCloudTestConfigChange> = {
  toolCallId?: string;
  operation: AgentTestOperation;
  changes: TChange[];
  userResponse?: 'confirmed' | 'rejected';
  agentAcknowledged?: boolean;
  isReadOnly?: boolean;
  isSelectAll: boolean;
  excludedItems: string[];
  selectedItems: string[];
};

export type AgentTestChangeRowIdentifiers = {
  assetId?: string;
  testType?: string;
  testId?: string;
  column?: string;
};

export const getAgentTestChangeRowId = (
  change: AgentTestChangeRowIdentifiers,
): string => {
  if (change.assetId !== undefined && change.testType !== undefined) {
    const base = `${change.assetId}__${change.testType}`;
    return change.column !== undefined ? `${base}__${change.column}` : base;
  }
  return change.testId ?? '';
};

export const isAgentCloudTestCreateChange = (
  change: AgentCloudTestConfigChange,
): change is AgentCloudTestCreateChange =>
  'assetId' in change && 'testType' in change;

export type AgentCodeTestConfigFields = {
  severity?: FieldChange<string | null>;
  description?: FieldChange<string | null>;
  tags?: FieldChange<string[]>;
  owners?: FieldChange<string[]>;
  params?: Record<string, FieldChange<unknown>>;
};

type AgentCodeTestLocator = {
  assetId: string;
  resourceName: string;
  sourceName?: string;
  column?: string;
  packageName?: string;
};

export type AgentCodeTestUpdateChange = {
  testId: string;
  testName: string;
} & AgentCodeTestLocator &
  AgentCodeTestConfigFields;

export type AgentCodeTestCreateChange = {
  testType: string;
} & AgentCodeTestLocator &
  AgentCodeTestConfigFields;

export type AgentCodeTestDeleteChange = {
  testId: string;
  testName: string;
  args?: Record<string, unknown>;
} & AgentCodeTestLocator &
  AgentCodeTestConfigFields;

export type AgentCodeTestConfigChange =
  | AgentCodeTestUpdateChange
  | AgentCodeTestCreateChange
  | AgentCodeTestDeleteChange;

export type AgentCodeTestConfigPlan =
  AgentTestConfigPlan<AgentCodeTestConfigChange>;

export const isAgentCodeTestCreateChange = (
  change: AgentCodeTestConfigChange,
): change is AgentCodeTestCreateChange => 'testType' in change;

export type AgentMergeIncidentsPlan = {
  toolCallId: string;
  goal: string;
  primaryIncidentId: string;
  /** Source incidents only (exclude primary); passed as `incident_ids` to the tool. */
  sourceIncidentIds: string[];
  title?: string;
  userSummary?: string;
  userResponse?: 'confirmed' | 'rejected';
  agentAcknowledged?: boolean;
};
